import React from "react";
import { AbsoluteFill, Audio, Sequence, interpolate, staticFile, useCurrentFrame, useVideoConfig } from "remotion";
import { fadeIn, slideUp, scaleIn, progressBar, slideLeft } from "../utils/animations";

export interface ExplainerStep {
  num: string;
  title: string;
  desc: string;
}

export interface ExplainerVideoProps {
  topic: string;
  problemStatement: string;
  solutionName: string;
  solutionDesc: string;
  steps: ExplainerStep[];
  result: string;
  resultStat?: string;
  ctaText: string;
  ctaSubtext?: string;
  accentColor?: string;
  voiceFile?: string;
}

const accent = (p: ExplainerVideoProps) => p.accentColor ?? "#38bdf8";

// ── Problem Scene ─────────────────────────────────────────────────────────────

const ProblemScene: React.FC<{ p: ExplainerVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;

  return (
    <AbsoluteFill style={{ background: "linear-gradient(160deg, #0a0000 0%, #1a0505 50%, #0a0000 100%)", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "#ef4444", boxShadow: "0 0 24px #ef444499" }} />
      <div style={{ position: "absolute", top: "35%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: "radial-gradient(ellipse, #ef444415 0%, transparent 65%)" }} />

      <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 28 : 20, color: "#ef4444", fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 24 }}>
        the problem
      </div>
      <div style={{ opacity: fadeIn(frame, 14), transform: `translateY(${slideUp(frame, 14)}px)`, fontSize: isVertical ? 80 : 58, fontWeight: 900, color: "#fef2f2", textAlign: "center", lineHeight: 1.15, maxWidth: "88%" }}>
        {p.problemStatement}
      </div>
      <div style={{ opacity: fadeIn(frame, 34), width: interpolate(frame, [34, 54], [0, 100], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }), height: 3, background: "#ef4444", borderRadius: 2, marginTop: 32 }} />

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e0000" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 80)}%`, background: "#ef4444" }} />
      </div>
    </AbsoluteFill>
  );
};


// ── Solution Scene ────────────────────────────────────────────────────────────

const SolutionScene: React.FC<{ p: ExplainerVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const ac = accent(p);

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: ac, boxShadow: `0 0 24px ${ac}` }} />
      <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: `radial-gradient(ellipse, ${ac}18 0%, transparent 65%)` }} />

      <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 28 : 20, color: ac, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>
        the solution
      </div>
      <div style={{ opacity: fadeIn(frame, 14), transform: `translateY(${slideUp(frame, 14)}px)`, fontSize: isVertical ? 90 : 66, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.1, marginBottom: 28 }}>
        {p.solutionName}
      </div>
      <div style={{ opacity: fadeIn(frame, 28), transform: `translateY(${slideUp(frame, 28, 18, 25)}px)`, fontSize: isVertical ? 40 : 30, color: "#94a3b8", textAlign: "center", lineHeight: 1.6, maxWidth: "84%" }}>
        {p.solutionDesc}
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 90)}%`, background: ac }} />
      </div>
    </AbsoluteFill>
  );
};

// ── Steps Scene ───────────────────────────────────────────────────────────────

const StepsScene: React.FC<{ p: ExplainerVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const ac = accent(p);
  const steps = p.steps.slice(0, 3);

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: `linear-gradient(${ac}44 1px, transparent 1px), linear-gradient(90deg, ${ac}44 1px, transparent 1px)`, backgroundSize: "60px 60px" }} />
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: ac }} />

      <AbsoluteFill style={{ padding: isVertical ? "100px 60px 60px" : "80px 80px 60px", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ opacity: fadeIn(frame, 5), fontSize: isVertical ? 26 : 20, color: ac, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", marginBottom: 32 }}>
          how it works
        </div>
        {steps.map((s, i) => (
          <div key={i} style={{
            opacity: fadeIn(frame, 15 + i * 22),
            transform: `translateX(${slideLeft(frame, 15 + i * 22)}px)`,
            display: "flex", alignItems: "flex-start", gap: 24,
            marginBottom: 24,
          }}>
            {/* Step number */}
            <div style={{ width: isVertical ? 72 : 56, height: isVertical ? 72 : 56, background: ac, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isVertical ? 32 : 24, fontWeight: 900, color: "#020617", flexShrink: 0 }}>
              {s.num}
            </div>
            <div style={{ flex: 1, paddingTop: 6 }}>
              <div style={{ fontSize: isVertical ? 40 : 28, fontWeight: 800, color: "#f1f5f9", marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: isVertical ? 30 : 22, color: "#64748b", lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </AbsoluteFill>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 175)}%`, background: ac }} />
      </div>
    </AbsoluteFill>
  );
};

// ── Result Scene ──────────────────────────────────────────────────────────────

const ResultScene: React.FC<{ p: ExplainerVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const ac = accent(p);

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: ac }} />
      <div style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%,-50%)", width: 700, height: 500, background: `radial-gradient(ellipse, ${ac}18 0%, transparent 65%)` }} />

      {p.resultStat && (
        <div style={{ opacity: fadeIn(frame, 6), transform: `scale(${scaleIn(frame, 6)})`, fontSize: isVertical ? 120 : 90, fontWeight: 900, color: ac, lineHeight: 1, marginBottom: 16, textShadow: `0 0 60px ${ac}66` }}>
          {p.resultStat}
        </div>
      )}
      <div style={{ opacity: fadeIn(frame, p.resultStat ? 22 : 10), transform: `translateY(${slideUp(frame, p.resultStat ? 22 : 10)}px)`, fontSize: isVertical ? 64 : 48, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.2, maxWidth: "88%" }}>
        {p.result}
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 80)}%`, background: ac }} />
      </div>
    </AbsoluteFill>
  );
};

// ── CTA Scene ─────────────────────────────────────────────────────────────────

const ExplainerCta: React.FC<{ p: ExplainerVideoProps }> = ({ p }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const isVertical = height > width;
  const ac = accent(p);

  return (
    <AbsoluteFill style={{ background: "#020617", fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", justifyContent: "center", alignItems: "center", flexDirection: "column", padding: 60 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: ac, boxShadow: `0 0 20px ${ac}` }} />

      <div style={{ opacity: fadeIn(frame, 8), transform: `translateY(${slideUp(frame, 8)}px)`, fontSize: isVertical ? 76 : 56, fontWeight: 900, color: "#f1f5f9", textAlign: "center", lineHeight: 1.15, marginBottom: 28 }}>
        {p.ctaText}
      </div>
      {p.ctaSubtext && (
        <div style={{ opacity: fadeIn(frame, 22), fontSize: isVertical ? 38 : 28, color: "#94a3b8", textAlign: "center", marginBottom: 36 }}>
          {p.ctaSubtext}
        </div>
      )}
      <div style={{ opacity: fadeIn(frame, 30), transform: `scale(${scaleIn(frame, 30)})`, background: ac, color: "#020617", padding: isVertical ? "22px 60px" : "16px 48px", borderRadius: 50, fontSize: isVertical ? 38 : 28, fontWeight: 900, boxShadow: `0 0 40px ${ac}66` }}>
        Learn More →
      </div>

      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: "#1e3a5f" }}>
        <div style={{ height: "100%", width: `${progressBar(frame, 80)}%`, background: ac }} />
      </div>
    </AbsoluteFill>
  );
};

// ── ExplainerVideo ────────────────────────────────────────────────────────────

export const ExplainerVideo: React.FC<ExplainerVideoProps> = (props) => {
  return (
    <AbsoluteFill>
      <Sequence from={0} durationInFrames={80}><ProblemScene p={props} /></Sequence>
      <Sequence from={80} durationInFrames={90}><SolutionScene p={props} /></Sequence>
      <Sequence from={170} durationInFrames={175}><StepsScene p={props} /></Sequence>
      <Sequence from={345} durationInFrames={80}><ResultScene p={props} /></Sequence>
      <Sequence from={425} durationInFrames={80}><ExplainerCta p={props} /></Sequence>
      {props.voiceFile && <Audio src={staticFile(props.voiceFile)} />}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
